<template>
  <module-card
    v-if="props.resourceType === RESOURCE_TYPE.LESSON"
    v-bind="moduleCardProps"
    @click="handleModuleCardClick"
    @view-details="handleViewLessonDetails"
  />
  <component
    :is="component"
    v-else
    :resource-data="props.item"
    :file-type="fileType"
    :size="props.size"
    :creation-params="props.creationParams"
    @handle-selected="handleSelected"
    @selection-changed="handleSelectionChanged"
    @selection-saved="handleSelectionSaved"
    @resource-unstarred="handleResourceUnstarred"
    @selected="handleItemSelected"
  />
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import FileCard from "@plugins/common/components/FileCard.vue";
import MediaCard from "@plugins/common/components/MediaCard.vue";
import QuestionCard from "@plugins/common/components/QuestionCard.vue";
import RubricCard from "@plugins/common/components/RubricCard.vue";
import ModuleCard from "@/components/ModuleCard.vue";
import { useModuleCard } from "@modules/resource-browse/composables/use-module-card.ts";
import {
  ADD_FROM_EXISTING_TYPE,
  MEDIA_TYPE,
  RESOURCE_TYPE,
} from "@modules/editor/service/editor.constant";
import { ADD_FROM_LOCATION } from "@modules/search/service/search.constant.ts";
import { RESOURCE_DOMAIN_PATH } from "@plugins/common/constant/resource.constant.ts";
import {
  DIRECT_CREATION_ACTION_TYPE,
  TYPE_OPTIONS,
} from "@modules/assignment/service/assignment.constant";
import SearchModuleDetailContentSubpage from "@modules/search/components/SearchModuleDetailContentSubpage.vue";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import { useRouting } from "@plugins/common/composables/routing.ts";
import { useRouter } from "vue-router";
import lessonApi from "@apis/editor.lesson.api";
import lessonEditHistoryApi from "@apis/lesson.edit.open.history.api";
import attemptLessonApi from "@/apis/resource.json.api";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { getLocationLabel } from "@/modules/resource-search/resource-search-helper";
import { ResourceDomain } from "@/types/lesson.ts";
import {
  AddLessonFromLibraryContentSubpageSelectionEvent,
  LessonSelectionModeEnum,
  LessonSelectionTypeEnum,
} from "@modules/resource-search/resource-search-types.ts";

interface CreationParams {
  isCreatingNewLesson?: boolean;
  ownerGroupId?: number | null;
  folderUuid?: string | null;
  targetPageIdx?: number | null;
  redirectAfterCreated?: boolean;
  addResourceActionBack?: () => void;
}

interface Props {
  item: object | null;
  resourceType: string | null;
  size?: string;
  creationParams?: CreationParams;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  handleSelected: [data: unknown];
  selectionChanged: [];
  selectionSaved: [data: unknown];
  resourceUnstarred: [data: unknown];
  selected: [data: AddLessonFromLibraryContentSubpageSelectionEvent];
}>();

interface SearchContext {
  addFromLocation?: string;
  addFromType?: string;
  createNewAssignment?: boolean;
  isFromPlanner?: boolean;
}
const searchContext = inject<SearchContext | undefined>("searchContext", undefined);

const { populateModuleCardPropsStarredResource } = useModuleCard();
const contentSubpageInstance = useContentSubpageInstance();
const { urlReady, target } = useRouting();
const router = useRouter();
const { passiveApiFailHandler } = useErrorHandler();
const { t } = useI18n();

const moduleCardProps = computed(() => populateModuleCardPropsStarredResource(props.item));

const resourceUuid = computed(() => {
  const item = props.item as { resourceUuid?: string; uuid?: string } | null;
  return item?.resourceUuid ?? item?.uuid;
});

const isContextCreateAssignment = computed(() => !!searchContext?.createNewAssignment);
const isAttachAttachmentToPersonalEvent = computed(() => !!searchContext?.isFromPlanner);
const isCreatingNewLesson = computed(() => props.creationParams?.isCreatingNewLesson ?? false);

const deriveSelectionMode = () => {
  const isFromGamification = searchContext?.addFromType === ADD_FROM_EXISTING_TYPE.GAME_BOOK;
  if (isFromGamification) return undefined;
  if (isContextCreateAssignment.value) {
    return LessonSelectionModeEnum.single;
  }
  return LessonSelectionModeEnum.multiple;
};

const deriveSelectionType = () => {
  const isFromGamification = searchContext?.addFromType === ADD_FROM_EXISTING_TYPE.GAME_BOOK;
  if (isFromGamification) return LessonSelectionTypeEnum.gamebook;
  if (isAttachAttachmentToPersonalEvent.value) return LessonSelectionTypeEnum.sectionOrLesson;
  if (isContextCreateAssignment.value) return LessonSelectionTypeEnum.lesson;
  return LessonSelectionTypeEnum.activityOrSection;
};

const customBackLabel = () => {
  if (isAttachAttachmentToPersonalEvent.value) {
    return t("calendar_event_attachment_attach_resource_label");
  }
  if (searchContext?.addFromLocation === ADD_FROM_LOCATION.STARRED_RESOURCES) {
    return t("add_from_starred_resources_label");
  }
  return undefined;
};

const handleModuleCardClick = () => {
  if (isContextCreateAssignment.value) {
    handleResourceToCreateAssignmentSelected();
  } else if (isAttachAttachmentToPersonalEvent.value) {
    handleViewLessonDetails();
  } else if (isCreatingNewLesson.value) {
    createLessonWithSourceLesson();
  } else {
    handleViewLessonDetails();
  }
};

const handleResourceToCreateAssignmentSelected = () => {
  const actionType =
    searchContext?.addFromLocation === ADD_FROM_LOCATION.STARRED_RESOURCES
      ? DIRECT_CREATION_ACTION_TYPE.FROM_STARRED_RESOURCES
      : DIRECT_CREATION_ACTION_TYPE.FROM_LIBRARY;
  const item = props.item as { title?: string } | null;

  attemptLessonApi
    .fetchLesson({ uuid: resourceUuid.value, status: "APPROVED" })
    .then((response) => {
      if (response.code !== "ok") {
        return;
      }
      const fetchLessonData = JSON.parse(response.data);
      emit("selectionSaved", {
        selectedValue: resourceUuid.value,
        lessonType: TYPE_OPTIONS.COURSE,
        actionType: actionType,
        title: item?.title,
        duplicatable: fetchLessonData.duplicatable,
        printable: fetchLessonData.printable,
        reattemptable: fetchLessonData.reattemptable,
      });
    });
};

const createLessonWithSourceLesson = async () => {
  try {
    const response = await lessonApi.createLessonWithSourceLesson(
      resourceUuid.value,
      props.creationParams?.folderUuid ?? null,
      [],
      [],
    );
    if (response.code !== "ok") {
      passiveApiFailHandler();
      return;
    }
    const newLessonUuid = response.data;
    const route = router.resolve({
      name: "EditorCover",
      params: {
        resourceDomain: RESOURCE_DOMAIN_PATH.MY_LIBRARY,
        lessonUuid: newLessonUuid,
        mode: "edit",
      },
    });
    target.value = "";
    urlReady.value = route.href;
    lessonEditHistoryApi.saveLessonEditHistory(newLessonUuid, null).then((historyResponse) => {
      if (historyResponse.code !== "ok") {
        passiveApiFailHandler();
      }
    }, passiveApiFailHandler);
  } catch (error) {
    passiveApiFailHandler(error);
  }
};

const handleViewLessonDetails = () => {
  const item = props.item as {
    title?: string;
    resourceDomain?: string;
    showParentSubpage?: boolean;
  } | null;
  const isRichSelection =
    isContextCreateAssignment.value || isAttachAttachmentToPersonalEvent.value;

  contentSubpageInstance.show(
    SearchModuleDetailContentSubpage,
    {
      selectionMode: deriveSelectionMode(),
      selectionType: deriveSelectionType(),
      customBackLabel: customBackLabel(),
      hasParentSubpage: item?.showParentSubpage ?? false,
      uuid: resourceUuid.value,
      title: item?.title,
      type: "LESSON",
      location: getLocationLabel((item?.resourceDomain as ResourceDomain) ?? null),
      canViewInNewTab: true,
      addFromLocation: searchContext?.addFromLocation,
      showMoreInformation: true,
      sectionId: props.creationParams?.isCreatingNewLesson ? null : undefined,
      folderUuid: props.creationParams?.folderUuid ?? null,
      targetPageIdx: props.creationParams?.targetPageIdx ?? null,
      redirectAfterCreated: props.creationParams?.redirectAfterCreated ?? false,
      ownerGroupId: props.creationParams?.ownerGroupId ?? null,
      isAttachingAttachment: isAttachAttachmentToPersonalEvent.value,
      addResourceActionBack: () => {
        props.creationParams?.addResourceActionBack?.();
      },
      actionBack: () => {
        /* no-op: intermediate parents (StarredResourceCardList / AddFromStarredResourcesContentSubpage)
           do not currently forward an `actionBack` event for this call site. */
      },
    },
    isRichSelection
      ? {
          resourceSelected: () => {
            if (isContextCreateAssignment.value) {
              handleResourceToCreateAssignmentSelected();
            }
          },
          selectionSelected: (sectionSelected: unknown) => {
            emit("selectionSaved", {
              selectedValue: resourceUuid.value,
              sectionId: sectionSelected,
            });
          },
          resourceUnstarred: handleResourceUnstarred,
          selected: handleItemSelected,
        }
      : {
          resourceSelected: (data: unknown) => emit("selectionSaved", data),
          resourceUnstarred: handleResourceUnstarred,
          selected: handleItemSelected,
        },
  );
};

// State
const component = ref<object | null>(null);
const fileType = ref<string | null>(props.resourceType);

// Computed
const isFileTypeComputed = computed(() => {
  switch (props.item?.fileType) {
    case MEDIA_TYPE.IMAGE:
    case MEDIA_TYPE.VIDEO:
      return false;
    case MEDIA_TYPE.AUDIO:
    case MEDIA_TYPE.EXCEL:
    case MEDIA_TYPE.HTML5:
    case MEDIA_TYPE.PDF:
    case MEDIA_TYPE.POWERPOINT:
    case MEDIA_TYPE.WORD:
    case MEDIA_TYPE.ZIP:
    case MEDIA_TYPE.OTHERS:
      return true;
    default:
      return false;
  }
});

// Methods
const handleSelected = (data: unknown) => {
  emit("handleSelected", data);
};

const handleSelectionChanged = () => {
  emit("selectionChanged");
};

const handleSelectionSaved = (data: unknown) => {
  emit("selectionSaved", data);
};

const handleResourceUnstarred = (data: unknown) => {
  emit("resourceUnstarred", data ?? resourceUuid.value);
};

const handleItemSelected = (data: AddLessonFromLibraryContentSubpageSelectionEvent) => {
  emit("selected", data);
};

const initializeComponent = () => {
  switch (props.resourceType) {
    case RESOURCE_TYPE.LESSON:
      component.value = null; // rendered via <module-card> above
      break;
    case RESOURCE_TYPE.MEDIA:
      fileType.value = props.item?.fileType;
      component.value = isFileTypeComputed.value ? FileCard : MediaCard;
      break;
    case RESOURCE_TYPE.QUESTION:
      component.value = QuestionCard;
      break;
    case RESOURCE_TYPE.RUBRIC:
      component.value = RubricCard;
      break;
    default:
      component.value = null;
  }
};

// Lifecycle
onMounted(() => {
  initializeComponent();
});
</script>
